import { useEffect, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ChevronDown,
  Facebook,
  Instagram,
  LogOut,
  Menu,
  ShieldCheck,
  UserRound,
  X,
  Youtube,
} from 'lucide-react';
import { Container } from '@/components/common/Container';
import { Logo } from '@/components/common/Logo';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Top navigation — pill-shaped center nav group, dual CTAs (Announcements +
 * Donate), and social icons. Matches the PWIC reference structure while
 * staying within the Serene Sanctuary palette (no purple/orange).
 *
 * Responsive disclosure:
 *   • mobile  (<md)   logo + hamburger only. Hamburger opens a slide-in
 *                     drawer with every link, sign-in/account row, and
 *                     social icons. Primary nav still also lives in the
 *                     thumb-friendly MobileBottomNav at the bottom edge.
 *   • md..lg          + pill nav group
 *   • lg              + Announcements CTA
 *   • xl              + Facebook/YouTube social icons
 */

interface DirectNavItem {
  type: 'link';
  to: string;
  label: string;
}

interface DropdownNavItem {
  type: 'dropdown';
  label: string;
  /** Where the trigger itself navigates when clicked (vs opening the menu). */
  to: string;
  items: { to: string; label: string }[];
}

type NavItem = DirectNavItem | DropdownNavItem;

const NAV: NavItem[] = [
  { type: 'link', to: '/', label: 'Home' },
  { type: 'link', to: '/community', label: 'Community' },
  { type: 'link', to: '/events', label: 'Events' },
  { type: 'link', to: '/about', label: 'About' },
  {
    type: 'dropdown',
    label: 'More',
    to: '/contact',
    items: [
      { to: '/prayer-times', label: 'Prayer Times' },
      { to: '/contact', label: 'Contact' },
      { to: '/ads', label: 'ADS' },
    ],
  },
];

/** Flattened nav list for the mobile drawer — dropdown items are inlined so
 *  every page is reachable from a single tap. */
const DRAWER_NAV: { to: string; label: string }[] = [
  { to: '/', label: 'Home' },
  { to: '/community', label: 'Community' },
  { to: '/events', label: 'Events' },
  { to: '/about', label: 'About' },
  { to: '/prayer-times', label: 'Prayer Times' },
  { to: '/contact', label: 'Contact' },
  { to: '/ads', label: 'ADS' },
];

export function TopNav() {
  const { user, isAuthenticated, hasRole, logout } = useAuth();
  const isAdmin = hasRole('ADMIN', 'SUPER_ADMIN');
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 z-50 w-full border-b border-outline-variant/20 bg-surface/80 backdrop-blur-xl transition-all"
    >
      <Container className="flex items-center justify-between gap-4 py-3">
        <Logo size={56} />

        {/* Center pill nav group — desktop only */}
        <div className="hidden items-center gap-1 rounded-full border border-outline-variant/20 p-1.5 md:flex">
          {NAV.map((item) =>
            item.type === 'link' ? (
              <PillLink key={item.to} to={item.to} label={item.label} />
            ) : (
              <PillDropdown key={item.label} item={item} />
            ),
          )}
        </div>

        {/* Right-side CTAs + social — desktop only */}
        <div className="hidden items-center gap-4 md:flex">
          {isAdmin && (
            <Link
              to="/admin/dashboard"
              className="hidden items-center gap-1 rounded-full bg-brand-teal/10 px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider text-brand-teal transition-colors hover:bg-brand-teal hover:text-white md:inline-flex"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Admin
            </Link>
          )}
          <Link
            to="/ads"
            className="hidden min-h-[44px] items-center px-3 py-2.5 text-[13px] font-bold text-on-surface transition-colors hover:text-brand-teal lg:inline-flex"
          >
            ADS
          </Link>
          <Link
            to="/donate"
            className="inline-flex min-h-[44px] items-center px-3 py-2.5 text-[13px] font-bold text-on-surface transition-colors hover:text-brand-teal"
          >
            Donate Now
          </Link>

          {isAuthenticated && user ? (
            <AccountMenu
              user={user}
              isAdmin={isAdmin}
              onSignOut={handleSignOut}
            />
          ) : (
            <Link
              to="/auth/login"
              className="inline-flex min-h-[44px] items-center px-3 py-2.5 text-[13px] font-bold text-on-surface transition-colors hover:text-brand-teal"
            >
              Sign In
            </Link>
          )}
          <div className="hidden items-center gap-2 pl-2 xl:flex">
            <span className="font-label-caps text-label-caps text-on-surface">Follow us!</span>
            <SocialIcon
              href="https://www.facebook.com/profile.php?id=61557094544027"
              label="MCCWA on Facebook"
            >
              <Facebook className="h-4 w-4" fill="currentColor" />
            </SocialIcon>
            <SocialIcon href="https://instagram.com" label="MCCWA on Instagram">
              <Instagram className="h-4 w-4" />
            </SocialIcon>
            <SocialIcon
              href="https://api.whatsapp.com/send?phone=%2B15403136087"
              label="MCCWA on WhatsApp"
            >
              <WhatsAppIcon className="h-4 w-4" />
            </SocialIcon>
            <SocialIcon href="https://youtube.com" label="MCCWA on YouTube">
              <Youtube className="h-4 w-4" fill="currentColor" />
            </SocialIcon>
          </div>
        </div>

        {/* Mobile: hamburger only. Logo stays on the left (above). */}
        <MobileMenu
          user={user}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          onSignOut={handleSignOut}
        />
      </Container>
    </nav>
  );
}

/* -------------------------------------------------------------------- */
/* Mobile drawer                                                        */
/* -------------------------------------------------------------------- */

interface MobileMenuProps {
  user: AccountMenuProps['user'] | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  onSignOut: () => void;
}

function MobileMenu({ user, isAuthenticated, isAdmin, onSignOut }: MobileMenuProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close the drawer on route change — without this, a user who taps a
  // link sees the drawer stay open until they manually tap the overlay.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label="Open menu"
        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-on-surface transition-colors hover:bg-secondary-container/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary md:hidden"
      >
        <Menu className="h-6 w-6" />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in data-[state=closed]:fade-out md:hidden" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-[61] flex w-full max-w-sm flex-col overflow-y-auto overflow-x-hidden bg-surface shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right md:hidden"
        >
          <div className="flex items-center justify-between border-b border-outline-variant/20 px-5 py-4">
            <Dialog.Title className="font-headline-sm text-lg font-bold italic text-on-surface">
              Menu
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-on-surface transition-colors hover:bg-secondary-container/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              <X className="h-6 w-6" />
            </Dialog.Close>
          </div>

          {/* Account row — at the top, since it changes the user's mental
              model of what other links will do (e.g. "Profile" only makes
              sense when signed in). */}
          {isAuthenticated && user ? (
            <div className="border-b border-outline-variant/20 px-5 py-4">
              <p className="font-body-md text-body-md font-semibold text-on-surface">
                {user.firstName} {user.lastName}
              </p>
              <p className="truncate text-sm text-on-surface-variant">{user.email}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {user.roles.map((r) => (
                  <span
                    key={r}
                    className="rounded-full bg-brand-teal/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-teal"
                  >
                    {r.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <nav aria-label="Mobile drawer" className="flex flex-col py-2">
            {DRAWER_NAV.map((item) => (
              <DrawerLink key={item.to} to={item.to} label={item.label} />
            ))}

            <div className="my-2 h-px bg-outline-variant/20" />

            {isAdmin && (
              <DrawerLink
                to="/admin/dashboard"
                label="Admin Dashboard"
                icon={<ShieldCheck className="h-5 w-5" />}
              />
            )}
            <DrawerLink
              to="/donate"
              label="Donate Now"
              emphasis
            />
            {isAuthenticated && user ? (
              <>
                <DrawerLink
                  to="/profile"
                  label="My Profile"
                  icon={<UserRound className="h-5 w-5" />}
                />
                <button
                  type="button"
                  onClick={onSignOut}
                  className="flex min-h-[44px] items-center gap-3 px-5 py-3 text-left text-[15px] font-semibold text-on-surface transition-colors hover:bg-secondary-container/30"
                >
                  <LogOut className="h-5 w-5" />
                  Sign Out
                </button>
              </>
            ) : (
              <DrawerLink to="/auth/login" label="Sign In" />
            )}
          </nav>

          <div className="mt-auto border-t border-outline-variant/20 px-5 py-5">
            <p className="mb-3 font-label-caps text-label-caps text-on-surface">Follow us!</p>
            <div className="flex items-center gap-2">
              <SocialIcon
                href="https://www.facebook.com/profile.php?id=61557094544027"
                label="MCCWA on Facebook"
              >
                <Facebook className="h-4 w-4" fill="currentColor" />
              </SocialIcon>
              <SocialIcon href="https://instagram.com" label="MCCWA on Instagram">
                <Instagram className="h-4 w-4" />
              </SocialIcon>
              <SocialIcon
                href="https://api.whatsapp.com/send?phone=%2B15403136087"
                label="MCCWA on WhatsApp"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </SocialIcon>
              <SocialIcon href="https://youtube.com" label="MCCWA on YouTube">
                <Youtube className="h-4 w-4" fill="currentColor" />
              </SocialIcon>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DrawerLink({
  to,
  label,
  icon,
  emphasis,
}: {
  to: string;
  label: string;
  icon?: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'flex min-h-[44px] items-center gap-3 px-5 py-3 text-[15px] font-semibold transition-colors',
          isActive
            ? 'bg-brand-teal/10 text-brand-teal'
            : 'text-on-surface hover:bg-secondary-container/30',
          emphasis && 'text-brand-teal',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

/* -------------------------------------------------------------------- */
/* Desktop internals                                                    */
/* -------------------------------------------------------------------- */

function PillLink({ to, label }: { to: string; label: string }) {
  const location = useLocation();

  // If the user is already on this route, react-router's NavLink is a no-op
  // and the page stays scrolled wherever it was. Scroll smoothly back to top
  // instead — fixes the "click Home and nothing happens" bug.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === to) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={handleClick}
      className={({ isActive }) => pillClass(isActive)}
    >
      {label}
    </NavLink>
  );
}

function PillDropdown({ item }: { item: DropdownNavItem }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          pillClass(false),
          'group inline-flex items-center gap-1 outline-none focus-visible:ring-2 focus-visible:ring-secondary',
        )}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {item.items.map((sub) => (
          <DropdownMenuItem key={sub.to} asChild>
            <Link to={sub.to}>{sub.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function pillClass(isActive: boolean) {
  return cn(
    'rounded-full px-4 py-2 text-[13px] transition-colors',
    isActive ? 'font-bold text-on-surface' : 'font-semibold text-on-surface hover:text-brand-teal',
  );
}

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noreferrer noopener"
      className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-fixed-dim text-on-secondary-container transition-transform hover:scale-110"
    >
      {children}
    </a>
  );
}

/** Inline WhatsApp logo — lucide-react doesn't ship a brand glyph. */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface AccountMenuProps {
  user: { firstName: string; lastName: string; email: string; roles: string[] };
  isAdmin: boolean;
  onSignOut: () => void;
}

function AccountMenu({ user, isAdmin, onSignOut }: AccountMenuProps) {
  const initials =
    `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account menu"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-teal text-[12px] font-bold text-white transition-colors hover:bg-brand-teal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed-dim focus-visible:ring-offset-2"
      >
        {initials}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="border-b border-outline-variant/20 px-3 pb-3 pt-2">
          <p className="font-body-md text-body-md font-semibold text-on-surface">
            {user.firstName} {user.lastName}
          </p>
          <p className="truncate text-sm text-on-surface-variant">{user.email}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {user.roles.map((r) => (
              <span
                key={r}
                className="rounded-full bg-brand-teal/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-teal"
              >
                {r.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admin/dashboard">
              <ShieldCheck className="h-4 w-4" />
              Admin Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <UserRound className="h-4 w-4" />
            My Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onSignOut();
          }}
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
